import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useOrganization } from "../store/organizationStore";
import { usePortalSchema, usePortalSchemaStore } from "../store/portalSchemaStore";
import { LoadingScreen } from "./LoadingScreen";

/**
 * Layout route that fetches the portal schema once for every enduser route beneath it and holds
 * rendering until it resolves, so pages can read settings (custom copy, `disableRegistration`,
 * `disableSMSForMFA`) without each one re-running its own fetch or flashing the defaults first.
 *
 * Gating on the schema itself rather than on `hasAttemptedFetch` is deliberate: `hasAttemptedFetch`
 * flips synchronously before the fetch is awaited, so a gate on it releases while the settings are
 * still unread — which is what let `/register` flash its Create Account button. `fetchSchema`
 * resolves to the built-in default whenever the URL is missing, rejects, 404s or fails validation,
 * so those cases fall through to default copy rather than waiting here.
 *
 * The one case this does not cover is a request that never settles: `fetchSchema` has no timeout,
 * so a stalled S3 GET holds every enduser route on the loading screen until the browser gives up.
 * Adding one belongs in `fetchSchema`, which the admin editor also uses — and there a timeout would
 * make its existing fall-back-to-default path reachable, risking an admin saving a default schema
 * over a real one. That tradeoff is deliberately left to its own change.
 */
export const PortalSchemaLoader = () => {
  const organization = useOrganization();
  const schema = usePortalSchema();
  const fetchSchema = usePortalSchemaStore((state) => state.fetchSchema);

  useEffect(() => {
    if (organization) {
      fetchSchema(organization.portalV2SchemaURL);
    }
  }, [organization, fetchSchema]);

  if (!schema) {
    return <LoadingScreen />;
  }

  return <Outlet />;
};
